import { CSSInterpolation } from '@emotion/css/create-instance'
import { css } from '@emotion/react'
import React from 'react'

import type { Icon16 } from './FoundationIcon/icons'
import { IconButton } from './IconButton'
import { Popover } from './Popover'
import { type AnnotationProps, RowUtils } from './Rows/RowUtils'
import { Type } from './Type'
import { useUniqueId } from './useUniqueId'

const styles = {
  mainContainer: css({
    alignItems: 'center',
    display: 'flex',
  }),
}

type Props = AnnotationProps & {
  /**
   * Leading label.
   */
  label: string
  /**
   * Trailing value.
   */
  value: string | number
  /**
   * Adds a trailing icon.
   */
  endIcon?: Icon16
  /**
   * Disables interactions, dims color palette.
   */
  isDisabled?: boolean
  /**
   * Determines the divider.
   */
  divider?: React.ComponentProps<typeof RowUtils.Section>['divider']
  /**
   * Overrides wrapper styles.
   */
  overrideRootStyles?: CSSInterpolation
  /**
   * Bolds the given value.
   */
  isValueBolded?: boolean
  'aria-labelledby'?: React.HTMLAttributes<HTMLDivElement>['aria-labelledby']
  'aria-describedby'?: React.HTMLAttributes<HTMLDivElement>['aria-describedby']
}

/**
 * A Web Row that displays simple label-value relationships.
 * It's static, but offers helper content in the form of a
 * question icon button and a popover.
 */
export function DataRow({
  annotation,
  annotationTitle,
  'aria-describedby': ariaDescribedBy,
  'aria-labelledby': ariaLabelledBy,
  divider = 'bottom',
  endIcon,
  isDisabled = false,
  isValueBolded = false,
  label,
  overrideRootStyles,
  value,
}: Props) {
  const annotationIconRef = React.useRef(null)
  const [isAnnotationPopoverOpen, setIsAnnotationPopoverOpen] =
    React.useState(false)

  const { colors } = RowUtils.useRowColors(isDisabled)

  const handleToggleAnnotationPopover = React.useCallback(() => {
    setIsAnnotationPopoverOpen(prev => !prev)
  }, [])
  const handleCloseAnnotationPopover = React.useCallback(() => {
    setIsAnnotationPopoverOpen(false)
  }, [])

  const popoverId = `${label}-data-row-description-${useUniqueId()}`

  return (
    <div
      aria-describedby={ariaDescribedBy}
      aria-disabled={isDisabled}
      aria-label={!ariaLabelledBy ? label : undefined}
      aria-labelledby={ariaLabelledBy}
      css={overrideRootStyles}
    >
      <RowUtils.Section
        divider={divider}
        mainContent={
          <div css={styles.mainContainer}>
            <Type.Text color={colors.primaryText} maxLines={3} size="M">
              {label}
            </Type.Text>
            {annotation && (
              <>
                <span ref={annotationIconRef}>
                  <IconButton
                    aria-controls={popoverId}
                    aria-describedby={popoverId}
                    aria-expanded={isAnnotationPopoverOpen}
                    // TODO: translate this when react-intl is available
                    aria-label={`Information on ${label}`}
                    aria-owns={popoverId}
                    foregroundColor={colors.secondaryText}
                    icon="Question16"
                    onClick={handleToggleAnnotationPopover}
                    state={isDisabled ? 'disabled' : undefined}
                    type="button"
                  />
                </span>
                <Popover
                  id={popoverId}
                  isOpen={isAnnotationPopoverOpen}
                  onClose={handleCloseAnnotationPopover}
                  targetEl={annotationIconRef.current}
                >
                  <Popover.Header title={annotationTitle || label} />
                  <Popover.Body content={annotation} />
                </Popover>
              </>
            )}
          </div>
        }
        trailingContent={
          <div css={RowUtils.contentGroupStyles}>
            <div>
              <Type.Text
                color={colors.primaryText}
                isBold={isValueBolded}
                maxLines={3}
                size="M"
              >
                {value}
              </Type.Text>
            </div>
            {endIcon && <RowUtils.Icon color={colors.icon} type={endIcon} />}
          </div>
        }
      />
    </div>
  )
}
